<template>
  <div class="container-fluid guest-signup-page">
    <div v-if="logoutSplash" class="logout-splash">
      <span>{{ logoutMessage }}</span>
    </div>

    <div class="container signup-shell">
      <div class="card1"></div>

      <div class="card signup-card">
        <div class="intro-copy">
          <p class="eyebrow">Guest signup</p>
          <h2>Almost there!</h2>
          <p class="lead">
            We will guide you through a short 5-step signup.
          </p>
        </div>

        <div class="signup-toggle">
          <span class="personal-info">
            <i class="fa fa-user"></i> Guest Signup
          </span>
        </div>

        <div class="signup-stepper">
          <div
            v-for="(item, index) in steps"
            :key="item.key"
            class="step-pill"
            :class="{ active: step === index + 1, done: step > index + 1 }"
          >
            <span class="step-index">{{ index + 1 }}</span>
            <span class="step-label">{{ item.title }}</span>
          </div>
        </div>

        <div class="step-card">
          <div class="step-copy">
            <span class="step-counter">Step {{ step }} of {{ steps.length }}</span>
            <h3>{{ currentStep.title }}</h3>
            <p>{{ currentStep.description }}</p>
          </div>

          <form @submit.prevent="handlePrimaryAction">
            <div v-if="step === 1" class="field-grid two-up">
              <div class="field">
                <label>Firstname</label>
                <input
                  type="text"
                  class="form-control input-text"
                  v-model.trim="firstname"
                  placeholder="Your first name"
                  autocomplete="given-name"
                />
              </div>

              <div class="field">
                <label>Lastname</label>
                <input
                  type="text"
                  class="form-control input-text"
                  v-model.trim="lastname"
                  placeholder="Your last name"
                  autocomplete="family-name"
                />
              </div>
            </div>

            <div v-else-if="step === 2" class="field-grid">
              <div class="field">
                <label>Email</label>
                <input
                  type="email"
                  class="form-control input-text"
                  v-model.trim="email"
                  placeholder="Your email address"
                  autocomplete="email"
                />
                <small class="field-hint">
                  We will send your verification code to this email.
                </small>
              </div>
            </div>

            <div v-else-if="step === 3" class="field-grid">
              <div class="field">
                <label>Verify OTP</label>
                <input
                  type="text"
                  class="form-control input-text"
                  v-model.trim="verify_code"
                  placeholder="Enter the code we sent to your email"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                />
                <div class="otp-status">
                  <span>This code expires in {{ formattedTimeLeft }}</span>
                  <button
                    type="button"
                    class="link-button"
                    @click="resendOtp"
                    :disabled="resending || timeLeft > 0"
                  >
                    {{ timeLeft > 0 ? "Resend available soon" : "Resend code" }}
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="step === 4" class="field-grid">
              <div class="field">
                <label>Phone</label>
                <input
                  type="tel"
                  class="form-control input-text"
                  v-model.trim="phone"
                  placeholder="Your phone number"
                  autocomplete="tel"
                />
              </div>
            </div>

            <div v-else-if="step === 5" class="field-grid">
              <div class="field">
                <label>Password</label>
                <input
                  type="password"
                  class="form-control input-text pwd"
                  v-model="password"
                  minlength="5"
                  maxlength="12"
                  placeholder="Create a password"
                  autocomplete="new-password"
                />
              </div>

              <div class="review-box">
                <p><strong>Name:</strong> {{ firstname }} {{ lastname }}</p>
                <p><strong>Email:</strong> {{ email }}</p>
                <p><strong>Phone:</strong> {{ phone }}</p>
              </div>
            </div>

            <div class="action-row">
              <button
                type="button"
                class="btn-back"
                v-if="step > 1"
                @click="back"
              >
                <i class="fa fa-arrow-left"></i> Back
              </button>

              <button class="btn-next" type="submit" :disabled="loading || resending">
                <span
                  v-if="loading && step === 5"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {{ primaryButtonText }}
                <i v-if="!loading || step !== 5" class="fa fa-arrow-right"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="card1"></div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert2";

export default {
  data() {
    return {
      steps: [
        {
          key: "name",
          title: "Name",
          description: "Tell us who you are.",
        },
        {
          key: "email",
          title: "Email",
          description: "This is where we will send the verification code.",
        },
        {
          key: "verify",
          title: "Verify",
          description: "Enter the one-time code from your inbox.",
        },
        {
          key: "phone",
          title: "Phone",
          description: "Add a contact number we can reach you on.",
        },
        {
          key: "password",
          title: "Password",
          description: "Choose a secure password and review your details.",
        },
      ],
      firstname: "",
      lastname: "",
      email: "",
      verify_code: "",
      phone: "",
      password: "",
      loading: false,
      resending: false,
      response: "",
      success_message: "",
      logoutSplash: false,
      logoutMessage: "",
      step: 1,
      timeLeft: 0,
      timer: null,
      bannerTimer: null,
      verifiedEmail: "",
      isEmailVerified: false,
      isResetting: false,
    };
  },

  computed: {
    currentStep() {
      return this.steps[this.step - 1] || this.steps[0];
    },
    formattedTimeLeft() {
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
    },
    primaryButtonText() {
      if (this.loading && this.step === 5) {
        return "Creating account...";
      }

      switch (this.step) {
        case 1:
          return "Continue";
        case 2:
          return "Send code";
        case 3:
          return "Verify code";
        case 4:
          return "Continue";
        case 5:
          return "Create account";
        default:
          return "Continue";
      }
    },
  },

  watch: {
    step() {
      this.saveForm();
    },
    firstname() {
      this.saveForm();
    },
    lastname() {
      this.saveForm();
    },
    email() {
      if (this.verifiedEmail && this.email !== this.verifiedEmail) {
        this.isEmailVerified = false;
      }
      this.saveForm();
    },
    verify_code() {
      this.saveForm();
    },
    phone() {
      this.saveForm();
    },
    password() {
      this.saveForm();
    },
    timeLeft() {
      this.saveForm();
    },
  },

  created() {
    const savedData = this.readSavedForm();
    if (savedData) {
      this.step = savedData.step || 1;
      this.firstname = savedData.firstname || "";
      this.lastname = savedData.lastname || "";
      this.email = savedData.email || "";
      this.verify_code = savedData.verify_code || "";
      this.phone = savedData.phone || "";
      this.password = savedData.password || "";
      this.timeLeft = typeof savedData.timeLeft === "number" ? savedData.timeLeft : 0;
      this.verifiedEmail = savedData.verifiedEmail || "";
      this.isEmailVerified = Boolean(savedData.isEmailVerified);
    }

    if (this.step === 3 && this.timeLeft > 0) {
      this.startCountdown(this.timeLeft);
    }
  },

  mounted() {
    localStorage.removeItem("wertyuiojhdfghhdrtfyguh");
  },

  beforeDestroy() {
    this.clearCountdown();
    this.clearBannerTimer();
  },

  methods: {
    readSavedForm() {
      try {
        return JSON.parse(localStorage.getItem("registration_data"));
      } catch (error) {
        return null;
      }
    },

    saveForm() {
      if (this.isResetting) {
        return;
      }

      localStorage.setItem(
        "registration_data",
        JSON.stringify({
          step: this.step,
          firstname: this.firstname,
          lastname: this.lastname,
          email: this.email,
          verify_code: this.verify_code,
          phone: this.phone,
          password: this.password,
          timeLeft: this.timeLeft,
          verifiedEmail: this.verifiedEmail,
          isEmailVerified: this.isEmailVerified,
        }),
      );
    },

    clearForm() {
      this.isResetting = true;
      this.step = 1;
      this.firstname = "";
      this.lastname = "";
      this.email = "";
      this.verify_code = "";
      this.phone = "";
      this.password = "";
      this.timeLeft = 0;
      this.verifiedEmail = "";
      this.isEmailVerified = false;
      this.clearCountdown();
      localStorage.removeItem("registration_data");
      this.isResetting = false;
    },

    flashMessage(message, duration = 2500) {
      this.logoutMessage = message;
      this.logoutSplash = true;
      this.clearBannerTimer();
      this.bannerTimer = setTimeout(() => {
        this.logoutSplash = false;
      }, duration);
    },

    clearBannerTimer() {
      if (this.bannerTimer) {
        clearTimeout(this.bannerTimer);
        this.bannerTimer = null;
      }
    },

    validateStep() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (this.step === 1) {
        if (!this.firstname) {
          this.flashMessage("Please provide your firstname");
          return false;
        }

        if (!this.lastname) {
          this.flashMessage("Please provide your lastname");
          return false;
        }
      }

      if (this.step === 2 && !emailPattern.test(this.email)) {
        this.flashMessage("Please enter a valid email address");
        return false;
      }

      if (this.step === 3 && !this.verify_code) {
        this.flashMessage("Please provide the verification code");
        return false;
      }

      if (this.step === 4 && !this.phone) {
        this.flashMessage("Please provide your phone number");
        return false;
      }

      if (this.step === 5 && !this.password) {
        this.flashMessage("Please provide your password");
        return false;
      }

      return true;
    },

    async handlePrimaryAction() {
      if (!this.validateStep()) {
        return;
      }

      if (this.step === 1) {
        this.step = 2;
        return;
      }

      if (this.step === 2) {
        await this.requestOtp();
        return;
      }

      if (this.step === 3) {
        await this.verifyOtp();
        return;
      }

      if (this.step === 4) {
        this.step = 5;
        return;
      }

      if (this.step === 5) {
        await this.submit();
      }
    },

    back() {
      if (this.step > 1) {
        this.step -= 1;
      }
    },

    async requestOtp() {
      const encodedEmail = encodeURIComponent(this.email);

      try {
        this.resending = true;
        const response = await this.$http.get(`/generate-token/${encodedEmail}`, {});

        if (response.data.success) {
          this.verify_code = "";
          this.isEmailVerified = false;
          this.verifiedEmail = "";
          this.step = 3;
          this.startCountdown(5 * 60);
          this.flashMessage(response.data.message || "Check your email for the OTP");
        } else {
          swal.fire({
            icon: "warning",
            title: "Unable to send code",
            text: response.data.message || "We could not send the verification code.",
          });
        }
      } catch (err) {
        swal.fire({
          icon: "error",
          title: "Server Error",
          text: err.response ? err.response.data.message : err.message,
        });
      } finally {
        this.resending = false;
      }
    },

    async resendOtp() {
      if (this.timeLeft > 0) {
        return;
      }

      await this.requestOtp();
    },

    async verifyOtp() {
      const encodedCode = encodeURIComponent(this.verify_code);
      const encodedEmail = encodeURIComponent(this.email);

      try {
        const response = await this.$http.get(
          `/validate-token/${encodedCode}/${encodedEmail}`,
          {},
        );

        if (response.data.success) {
          this.isEmailVerified = true;
          this.verifiedEmail = this.email;
          this.step = 4;
          this.flashMessage(response.data.message || "Email verified");
          return;
        }

        this.flashMessage(response.data.message || "Email verification failed");
      } catch (err) {
        swal.fire({
          icon: "error",
          title: "Server Error",
          text: err.response ? err.response.data.message : err.message,
        });
      }
    },

    async submit() {
      try {
        if (!this.firstname || !this.lastname || !this.email || !this.phone || !this.password) {
          swal.fire({
            icon: "warning",
            title: "Error...",
            width: 450,
            text: "Please fill all required fields.",
          });
          return;
        }

        this.loading = true;

        this.response = await this.$http.post(
          "/guest_sign_up",
          {
            firstname: this.firstname,
            lastname: this.lastname,
            email: this.email,
            phone: this.phone,
            password: this.password,
            role: "guest",
            email_status: "verified",
          },
          {
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              "Content-Type": "application/json",
            },
          },
        );

        this.loading = false;

        if (this.response.data.success) {
          swal.fire({
            icon: "success",
            width: 450,
            text: this.response.data.message,
          });

          await this.login(this.email, this.password);
          this.clearForm();
        } else {
          swal.fire({
            icon: "warning",
            width: 450,
            text: this.response.data.message,
          });
        }
      } catch (err) {
        this.loading = false;
        swal.fire({
          icon: "error",
          title: "Server Error",
          text: err.response ? err.response.data.message : err.message,
        });
      }
    },

    async login(email, password) {
      try {
        const response = await this.$http.post(
          "/login",
          {
            email: email,
            password: password,
          },
          {
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.data.success) {
          swal.fire({
            icon: "error",
            title: "Error",
            width: 450,
            height: 5,
            text: response.data.message,
          });
          return;
        }

        const token = response.data.token;
        const role = response.data.data[0].role;
        const userid = response.data.data[0].id;
        const isChat = response.data.isChat;
        const responseEmail = response.data.email;

        await localStorage.setItem("wertyuiojhdfghhdrtfyguh", token);
        await localStorage.setItem("role", role);
        await localStorage.setItem("userid", userid);
        await localStorage.setItem("isChat", isChat);
        await localStorage.setItem("email", responseEmail);
       
        setTimeout(() => {
            window.location="/";
        }, 2500);
        
      } catch (err) {
        swal.fire({
          icon: "error",
          title: "Error",
          width: 450,
          height: 5,
          text: err.response ? err.response.data.message : err.message,
        });

        setTimeout(() => {
          this.$router.push("/login");
        }, 2500);
      }
    },

    startCountdown(initialSeconds = 5 * 60) {
      this.clearCountdown();
      this.timeLeft = initialSeconds;
      this.timer = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft -= 1;
        } else {
          this.clearCountdown();
        }
      }, 1000);
    },

    clearCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.guest-signup-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(211, 62, 116, 0.18), transparent 35%),
    linear-gradient(180deg, #0f0c15 0%, #0a070f 100%);
  color: #ffffff;
  padding: 32px 0 56px;
}

.logout-splash {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 15px;
  text-align: center;
  background-color: #0a070f;
  color: #ffffff;
  font-weight: 700;
  z-index: 9999;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}

.signup-shell {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 24px;
  width: 100%;
  margin-top: 54px;
}

.card1 {
  flex: 1;
  min-height: 1px;
}

.signup-card {
  flex: 0 1 680px;
  width: 100%;
  background: rgba(25, 24, 27, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 32px;
  padding: 28px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(14px);
}

.intro-copy {
  text-align: center;
}

.eyebrow {
  margin-bottom: 8px;
  color: #f7a7c0;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 11px;
  font-weight: 700;
}

h2 {
  margin: 0;
  font-family: "Raleway", sans-serif;
  font-weight: 800;
  font-size: 38px;
  color: #ffffff;
}

.lead {
  margin: 10px auto 0;
  max-width: 520px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 16px;
  line-height: 1.6;
}

.signup-toggle {
  margin-top: 24px;
  width: 100%;
  padding: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
}

.personal-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  border-radius: 999px;
  background: linear-gradient(90deg, #d33e74 0%, #ff6b88 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
}

.signup-stepper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 10px;
  margin-top: 22px;
}

.step-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.68);
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.step-pill.active {
  background: rgba(211, 62, 116, 0.16);
  border-color: rgba(211, 62, 116, 0.45);
  color: #ffffff;
}

.step-pill.done {
  background: rgba(61, 182, 92, 0.15);
  color: #ffffff;
}

.step-index {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  font-size: 12px;
  font-weight: 700;
  flex: none;
}

.step-pill.active .step-index {
  background: #ffffff;
  color: #0a070f;
}

.step-pill.done .step-index {
  background: #3db65c;
  color: #ffffff;
}

.step-label {
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.step-card {
  margin-top: 22px;
}

.step-copy {
  margin-bottom: 18px;
}

.step-counter {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: #f7a7c0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.step-copy h3 {
  margin: 12px 0 6px;
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
}

.step-copy p {
  margin: 0;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.55;
}

.field-grid {
  display: grid;
  gap: 16px;
}

.two-up {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.field label {
  display: block;
  margin: 0 0 10px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

.field-hint {
  display: block;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 12px;
  line-height: 1.5;
}

.input-text {
  box-sizing: border-box;
  width: 100%;
  height: 54px;
  padding: 0 20px;
  border-radius: 999px;
  background: #0a070f;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #ffffff;
  font-size: 14px;
}

.input-text:focus {
  border-color: #d33e74;
  box-shadow: 0 0 0 0.15rem rgba(211, 62, 116, 0.2);
}

.otp-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 12px;
}

.link-button {
  border: none;
  background: none;
  color: #f7a7c0;
  font-weight: 700;
  padding: 0;
}

.link-button:disabled {
  color: rgba(255, 255, 255, 0.35);
  cursor: not-allowed;
}

.review-box {
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.review-box p {
  margin: 0 0 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
}

.review-box p:last-child {
  margin-bottom: 0;
}

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 22px;
}

.btn-next,
.btn-back {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 52px;
  border-radius: 999px;
  font-family: "Source Sans 3", sans-serif;
  font-size: 16px;
  font-weight: 700;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.btn-next {
  background: #ffffff;
  border: none;
  color: #0a070f;
}

.btn-back {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.btn-next:hover,
.btn-back:hover {
  transform: translateY(-1px);
}

.btn-next:disabled,
.btn-back:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media screen and (max-width: 768px) {
  .guest-signup-page {
    padding: 18px 0 40px;
  }

  .signup-shell {
    gap: 0;
    margin-top: 60px;
  }

  .signup-card {
    padding: 22px 18px;
    border-radius: 26px;
  }

  h2 {
    font-size: 30px;
  }

  .lead {
    font-size: 13px;
  }

  .signup-stepper {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .step-pill {
    min-height: 44px;
  }

  .step-label {
    white-space: normal;
  }

  .two-up {
    grid-template-columns: 1fr;
  }

  .action-row {
    flex-direction: column-reverse;
  }

  .btn-next,
  .btn-back {
    width: 100%;
  }

  .otp-status {
    flex-direction: column;
    align-items: flex-start;
  }

  .card1 {
    display: none;
  }
}
</style>
